<script lang="ts">
   import { defineComponent } from "vue";
   import {
      RangeSlider,
      ForeclosureData,
      ExistingInvestment,
      PreForeclosureNotice,
      PropertyToolBar,
      NumericalInput,
      AnalysisRequestHeader,
      NotificationsBar,
      SessionState,
      //#region Property tab
      RentalRate,
      PropertyValue,
      MlsHistory,
      MlsListingInfo,
      MlsAgentInfo,
      BrandingHeader,
      PropertyDetails,
      EditPropertyDetailsDialog,
      PropertyFiles,
      PropertyPhotos,
      PropertyInstances,
      //#endregion
      //#region Proforma tab
      ProformaTab,
      ProjectedAccumulationOfWealth,
      AppreciationAndPrincipalReduction,
      //#endregion
      ScenariosTab,
      //#region Rent vs Sell tab
      RentVsSellTab,
      //#endregion
      //#region Schools tab
      SchoolMap,
      SchoolInformation,
      //#endregion
      //#region Community tab
      CommunityData,
      //#endregion
      //#region Records tab
      RecordsTab,
      //#endregion
      //#region Expense Ledger tab
      ExpenseLedger,
      //#endregion
      //#region Parameters
      ParametersTab,
      //#endregion
      //#region Flip
      FlipTab,
      BRRRRTab,
      //#endregion
      //#region Asset Review Tab
      AssetReviewTab,
      //#endregion
      showTab,
      currentYear,
      gaugeOptions,
      showError,
      showSuccess,
      trackTabs,
      formatters,
      ErrorIds,
      Exception,
      RentalType,
      useAuthStore,
      useAccountStore,
      usePropertyStore,
      usePortfolioInsightsStore,
      rescoverApi,
      RequestStatus,
      InvestmentStrategy,
      PropertyType,
      StandardIncomeExpenseAccounts,
      AnalysisMode,
      ProformaTotalInvestment,
      FlipInitialInvestment,
   } from '@rescover/ui-library'
   import type {
      PropertyDetailsModel,
      CalculatorTweaksModel,
      PropertyInfo,
      YearRawResult,
      YearNormalizedResult,
      YearNormalizedPreciseResult,
      ReportingType,
      ReportPropertyModel,
      Filing,
      BrandingModel,
      FileDownloadResponse,
      RentalTypeComparisonTypes
   } from '@rescover/ui-library';
   import $ from 'jquery';
   import { Chart, type LegendOptions, DoughnutController, ArcElement } from 'chart.js';
   import _, { DebouncedFunc } from 'lodash';
   import { type PropertyInfo as GlobalPropertyInfo, type UserDefaults } from '../../interfaces';
   import { MissingValueType } from "~/interfaces/MissingValueType";
   import { type AxiosResponse } from 'axios';
   import { Fancybox } from "@fancyapps/ui";

   import { mapState, mapStores } from "pinia";

   const componentDebug = true;

   Chart.register(DoughnutController, ArcElement)

   // Chart colors
   const green = '#23B14C';
   const red = '#EE1C25';
   const blue = '#3C67BE';
   const black = '#000000';
   const softblue = '#7092BF';
   const orange = '#E76627';
   const yellow = '#FFC023';
   const purple = '#A34AA4';
   const grey1 = '#DADADA';
   const grey2 = '#E4E4E4';
   const grey3 = '#D0D0D0';

   declare global {
      interface Window {
         preview: string
         schools: Array<string>
         listingValues: GlobalPropertyInfo
         userDefaults: UserDefaults
         calcParamsOrig: CalculatorTweaksModel
         calcParams: CalculatorTweaksModel
         query_changes: boolean
         isFavorite?: boolean
         isIgnored?: boolean
         isCustomized: boolean
         analysis?: string
         analysisRequestId?: number
         share?: string
         myMarketplace?: boolean
         client?: number
         mls?: string
         isIdx: boolean
         portfolioId?: number
      }
   }
   declare module '@vue/runtime-core' {
      interface ComponentCustomProperties {
         charts: Charts;
         handleDebouncedScroll: DebouncedFunc<(event: any) => void>;
         map: google.maps.Map;
         typingTimer: number;
         modalReport: bootstrap.Modal
         // Help sidebar
         offCanvasEl: HTMLElement
         tooltipEl: HTMLElement
         infoPopContent: HTMLElement
      }
   }

   type Charts = {
      capRateDonutChart?: Chart<'doughnut', number[], string>,
      cocDonutChart?: Chart<'doughnut', number[], string>,
      irrDonutChart?: Chart<'doughnut', number[], string>,
   };
   type ISchoolInfo = {
      elementary?: number;
      intermediate?: number;
      jrHigh?: number;
      high?: number;
   }
   type IProperty = {
      propId?: number
      mlsSource?: string
      mlsNumber?: string
      //mlsId?: number
      sqFt: number
      propertyValue: number
      year?: number
      address: string
      city: string
      state: string
      zip: string
      isFavorite?: boolean
      isIgnored?: boolean
      isActive: boolean
      isPending: boolean
      isOffmarket: boolean
      status: string
      soldDate?: Date
      hideRentalAvm: boolean
      hideValueAvm: boolean
      virtualTourUrl?: string
      isActiveMarketplaceListing: boolean
      occupied?: boolean
   }
   type vData = {
      pageError?: boolean
      originalPurchasePrice?: number
      property: PropertyInfo;
      preview: string;
      listPrice: number;
      email: {
         address: string;
         ccFriend: boolean;
      };
      modelView: AnalysisMode;
      isPercentage: boolean;
      missingValueModalTitle?: string
      missingValueModalDesc?: string
      missingValueModalValue?: number
      missingValueModalType?: MissingValueType
      analysis?: string
      analysisRequestId?: number
      myMarketplace?: boolean
      isCustomized: boolean
      share?: string
      monthlyRentalMin?: number
      monthlyRentalMax?: number
      strRentalMin?: number
      strRentalMax?: number
      purchasePriceMin?: number
      purchasePriceMax?: number
      flipAfterRepairValueMin?: number
      flipAfterRepairValueMax?: number
      propertyValueMin?: number
      propertyValueMax?: number
      renderKey: number
      communityLoading: boolean
      reportingIssueType?: ReportingType
      reporting: boolean
      preForeclosureFiling?: Filing
      schoolMapHeight: number
      clientId?: number
      onBehalfOf: boolean
      branding?: BrandingModel
      showInvestmentDetails: boolean,
      portfolioId?: number
      portfolioName?: string
      isUsingCustomFinancialParams: boolean
      flipInit: boolean
      tooltipHoldPeriod: number
   }

   export default defineComponent({
      name: 'property-page',
      //errorCaptured(err: unknown, instance: ComponentPublicInstance | null, info: string) {
      //    console.log('errorCaptured', err, instance, info);
      //    return true;
      //},
      data(): vData {
         return {
            pageError: false,
            renderKey: 0,
            originalPurchasePrice: undefined,
            property: {
               propId: window.listingValues.PropId,
               isCustom: undefined,
               address: {
                  address: window.listingValues.Address, // Obsolete
                  street: window.listingValues.Address,
                  city: window.listingValues.City,
                  state: window.listingValues.State,
                  zip: window.listingValues.Zip,
               },
               facts: {
                  squareFootage: window.listingValues.SqFt,
                  year: window.listingValues.Year,
               },
               financial: {
                  propertyValue: window.listingValues.PropertyValue,
               },
               mlsSource: {
                  shortCode: window.listingValues.MlsSource,
                  mlsNumber: window.listingValues.MlsNumber,
                  hideRentalAvm: window.listingValues.HideRentalAvm,
                  hideValueAvm: window.listingValues.HideValueAvm,
                  isActive: window.listingValues.IsActive,
                  isActiveMarketplaceListing: window.listingValues.IsActiveMarketplaceListing,
                  isPending: window.listingValues.IsPending,
                  isOffmarket: window.listingValues.IsOffmarket,
                  status: window.listingValues.Status,
                  virtualTourUrl: window.listingValues.VirtualTourUrl,
                  occupied: window.listingValues.Occupied,
               },

               /*
               isFavorite: window.isFavorite,
               isIgnored: window.isIgnored,
               soldDate: window.listingValues.SoldDate ? new Date(window.listingValues.SoldDate) : undefined,
               */
            },
            preview: window.preview,
            listPrice: window.listingValues.ListPrice,
            email: {
               address: '',
               ccFriend: false
            },
            modelView: AnalysisMode.Basic,
            isPercentage: false,
            //#region Missing Value Modal
            missingValueModalDesc: undefined,
            missingValueModalTitle: undefined,
            missingValueModalType: undefined,
            missingValueModalValue: undefined,
            //#endregion
            analysis: window.analysis,
            analysisRequestId: window.analysisRequestId,
            myMarketplace: window.myMarketplace,
            isCustomized: window.isCustomized,
            share: window.share,
            monthlyRentalMin: window.calcParams.monthlyRentalYear1 * 0.70,
            monthlyRentalMax: window.calcParams.monthlyRentalYear1 * 1.10,
            strRentalMin: window.calcParams.strRentalRate * 0.70,
            strRentalMax: window.calcParams.strRentalRate * 1.10,
            purchasePriceMin: window.calcParams.purchaseprice * 0.70,
            purchasePriceMax: window.calcParams.purchaseprice * 1.10,
            propertyValueMin: window.listingValues.PropertyValue * 0.70,
            propertyValueMax: window.listingValues.PropertyValue * 1.10,
            flipAfterRepairValueMin: undefined,
            flipAfterRepairValueMax: undefined,
            communityLoading: true,
            reportingIssueType: undefined,
            reporting: false,
            preForeclosureFiling: undefined,
            schoolMapHeight: 400,
            clientId: undefined,
            onBehalfOf: false,
            branding: {
               company: ''
            },
            showInvestmentDetails: false,
            portfolioId: undefined,
            portfolioName: undefined,
            isUsingCustomFinancialParams: false,
            flipInit: false,
            tooltipHoldPeriod: 0,
         } as vData;
      },
      components: {
         RangeSlider,
         RentalRate,
         PropertyValue,
         MlsHistory,
         MlsListingInfo,
         MlsAgentInfo,
         //PortfolioFinder,
         ScenariosTab,
         ProjectedAccumulationOfWealth,
         AppreciationAndPrincipalReduction,
         SchoolMap,
         SchoolInformation,
         CommunityData,
         //TaxAsessorData,
         //CurrentPropertyRecords,
         //AllPropertyRecords,
         ForeclosureData,
         BrandingHeader,
         PropertyDetails,
         EditPropertyDetailsDialog,
         ExistingInvestment,
         PreForeclosureNotice,
         PropertyFiles,
         AnalysisRequestHeader,
         PropertyPhotos,
         ToolBar: PropertyToolBar,
         NumericalInput,
         ParametersTab,
         Ledger: ExpenseLedger,
         FlipTab,
         BrrrrTab: BRRRRTab,
         RentVsSellTab,
         ProformaTab,
         NotificationsBar,
         SessionState,
         PropertyInstances,
         //PortfolioFinderInfo,
         //PortfolioFinderTargetValue,
         RecordsTab,
         TotalInvestment: ProformaTotalInvestment,
         FlipInitialInvestment,
         AssetReviewTab
      },
      computed: {
         apiKey(): string | undefined {
            return this.analysis ?? this.share;
         },
         usingApiKey(): boolean {
            const key = this.analysis ?? this.share ?? '';
            return key != "";
         },
         totalCost(): number {
            if (this.isShortTerm)
               return this.calculatedResults?.shortTermSummary?.totalCost;
            return this.calculatedResults?.totalCost;
         },
         rehabExpense(): number {
            if (this.isShortTerm)
               return this.calculatedResults?.shortTermSummary?.rehab;
            return this.calculatedResults?.rehab;
         },
         isAdvancedMode(): boolean {
            return this.account?.defaultAnalysisMode == AnalysisMode.Advanced;
         },
         capRate(): number | undefined {
            if (this.calculatedResults) {
               if (this.isShortTerm)
                  return this.calculatedResults?.shortTerm?.find(q => q.year == this.firstProformaYear)?.capRate;
               if (this.isPadSplit)
                  return this.calculatedResults?.padSplit?.find(q => q.year == this.firstProformaYear)?.capRate;
               return this.calculatedResults?.normalized?.find(q => q.year == this.firstProformaYear)?.capRate;
            }
            return undefined;
         },
         idealIRR(): number | undefined {
            if (this.calculatedResults && this.hideIrr == false) {
               if (this.isShortTerm)
                  return this.calculatedResults.shortTermSummary?.idealIrr;
               else
                  return this.calculatedResults.idealIRR;

            }
            return undefined;
         },
         idealIRRAfterTax(): number | undefined {
            if (this.calculatedResults && this.hideIrr == false) {
               if (this.isShortTerm)
                  return this.calculatedResults.shortTermSummary?.idealIrrAfterTax;
               else
                  return this.calculatedResults.longTermSummary?.idealIrrAfterTax;

            }
            return undefined;
         },
         idealIRRYear(): number | undefined {
            if (this.calculatedResults && this.hideIrr == false) {
               if (this.isShortTerm) {
                  const year = this.calculatedResults.shortTermSummary?.idealHoldYear;
                  return this.calculatedResults?.shortTerm?.find((a) => a.year == year)?.fiscalYear;
               }
               else {
                  const year = this.calculatedResults.idealHoldYear;
                  return this.calculatedResults?.raw?.find((a) => a.year == year)?.fiscalYear;
               }
            }
            return undefined;
         },
         workingCapital(): number {
            var total = 0;
            if (this.isShortTerm) {
               // TODO: Add STR Stabilization Reserve when we calculate it
               //if (this.calculatedResults && this.calculatedResults.stabilizationReserve) {
               //    total += this.calculatedResults.stabilizationReserve;
               //}
               if (this.calcParams?.strWorkingCapitalReserve) {
                  total += this.calcParams.strWorkingCapitalReserve;
               }
            } else {
               if (this.calculatedResults?.stabilizationReserve) {
                  total += this.calculatedResults.stabilizationReserve;
               }
               if (this.calcParams?.workingCapitalReserve) {
                  total += this.calcParams.workingCapitalReserve;
               }
            }
            return total;
         },
         monthlyRental(): number | undefined {
            if (this.calcParams?.monthlyRentalYear1 > 1) {
               return this.calcParams.monthlyRentalYear1;
            }
            return undefined;
         },
         flipAfterRepairValueSF(): number | undefined {
            if (this.flipParams?.afterRepairValue)
               return this.flipParams.afterRepairValue / this.property.facts.squareFootage;

            return undefined;
         },
         priceSF(): number | '-' {
            if (this.calcParams?.purchaseprice) {
               return this.calcParams.purchaseprice / this.property.facts.squareFootage;
            }
            return '-';
         },
         priceSFPropValue(): number | undefined {
            if (this.propertyValue) {
               return this.propertyValue / this.property.facts.squareFootage;
            }
            return undefined;
         },
         rentalSF(): number | '-' {
            if (this.calcParams?.monthlyRentalYear1 >= 1) {
               return this.calcParams.monthlyRentalYear1 / this.property.facts.squareFootage;
            }
            return '-';
         },
         strRentalRateSF(): number | '-' {
            if (this.calcParams?.strRentalRate) {
               return this.calcParams.strRentalRate / this.property.facts.squareFootage;
            }
            return '-';
         },
         downpaymentStandard(): boolean {
            const a = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
            return (a.find((a) => a == this.downpaymentWhole) ?? 0) > 0;
         },
         autoHoldPeriod: {
            get(): boolean {
               return this.calcParams.hold == 0;
            },
            set(value: boolean) {
               //console.log('autoHoldPeriod', value);
               if (value)
                  this.calcParams.hold = 0;
               else
                  this.calcParams.hold = 10;
            }
         },
         //#region Calc Params formatting
         propertyValue: {
            get(): number {
               if (this.isExistingInvestment)
                  return this.calcParams?.propertyValueEoY;
               return this.property?.financial?.propertyValue;
            },
            set(value) {
               if (this.isExistingInvestment)
                  this.calcParams.propertyValueEoY = Math.trunc(value);
               else
                  this.property.financial.propertyValue = Math.trunc(value);
            }
         },
         purchasePrice: {
            get(): string {
               return formatters.number(this.calcParams.purchaseprice);
            },
            set(value) {
               this.calcParams.purchaseprice = formatters.parse(value);
            }
         },
         monthlyRentalsFirst: {
            get(): string {
               if (this.calcParams?.monthlyRentalYear1 >= 1) {
                  return formatters.number(this.calcParams.monthlyRentalYear1);
               }
               return '';
            },
            set(value) {
               this.calcParams.monthlyRentalYear1 = formatters.parse(value);
            }
         },
         monthlyRentalsSecond: {
            get(): string {
               if (this.calcParams?.monthlyRentalYear2 >= 2) {
                  return formatters.number(this.calcParams.monthlyRentalYear2);
               }
               return '';
            },
            set(value) {
               if (value === '')
                  this.calcParams.monthlyRentalYear2 = undefined;
               this.calcParams.monthlyRentalYear2 = formatters.parse(value);
            }
         },
         monthlyRentalsThird: {
            get(): string {
               if (this.calcParams?.monthlyRentalYear3 >= 3) {
                  return formatters.number(this.calcParams.monthlyRentalYear3);
               }
               return '';
            },
            set(value) {
               if (value === '')
                  this.calcParams.monthlyRentalYear3 = undefined;
               this.calcParams.monthlyRentalYear3 = formatters.parse(value);
            }
         },
         rehabAmount: {
            get(): string {
               return formatters.number(this.calcParams.rehabamount);
            },
            set(value) {
               this.calcParams.rehabamount = formatters.parse(value);
            }
         },
         workingCapitalReserve: {
            get(): string {
               return formatters.number(this.calcParams.workingCapitalReserve);
            },
            set(value) {
               this.calcParams.workingCapitalReserve = formatters.parse(value);
            }
         },
         rehabDays: {
            get(): string {
               return formatters.number(this.calcParams.rehabdays);
            },
            set(value) {
               this.calcParams.rehabdays = formatters.parse(value);
            }
         },
         arv: {
            get(): string {
               if (this.calcParams.arv)
                  return formatters.number(this.calcParams.arv);
               return '';
            },
            set(value) {
               if (value === '')
                  this.calcParams.arv = null;
               this.calcParams.arv = formatters.parse(value);
            }
         },
         downpayment: {
            get(): string {
               if (this.isExistingInvestment) {
                  const purchasePrice = this.purchaseFacts.purchasePrice ?? 0;
                  const loanAmount = this.purchaseFacts.loan?.amount ?? 0;
                  if (purchasePrice > 0)
                     return formatters.number((1 - (loanAmount / purchasePrice)) * 100);
               }
               return formatters.number(this.calcParams.downpayment * 100);
            },
            set(value: string) {
               if (DEBUG && componentDebug)
                  console.log('downpayment', value);
               this.calcParams.downpayment = formatters.parse(value) / 100;
               this.calcParams.loanAmount = Math.trunc(this.calcParams.purchaseprice * (1 - this.calcParams.downpayment));
            }
         },
         downpaymentRaw: {
            get(): number {
               if (this.isExistingInvestment) {
                  const purchasePrice = this.purchaseFacts?.purchasePrice ?? 0;
                  const loanAmount = this.purchaseFacts?.loan?.amount ?? 0;
                  if (purchasePrice > 0)
                     return (1 - (loanAmount / purchasePrice));
               }
               if (this.calcParams.downpayment > 1)
                  return this.calcParams.downpayment / 100;
               return this.calcParams.downpayment;
            },
            set(value: number) {
               if (DEBUG && componentDebug)
                  console.log('downpaymentRaw', value);
               if (value > 1)
                  this.calcParams.downpayment = value / 100;
               else
                  this.calcParams.downpayment = value;
               this.calcParams.loanAmount = Math.trunc(this.calcParams.purchaseprice * (1 - this.calcParams.downpayment));
            }
         },
         downpaymentWhole: {
            get(): number {
               if (this.isExistingInvestment) {
                  const purchasePrice = this.purchaseFacts.purchasePrice ?? 0;
                  const loanAmount = this.purchaseFacts.loan?.amount ?? 0;
                  if (purchasePrice > 0)
                     return Math.round((1 - (loanAmount / purchasePrice)) * 100);
               }
               if (this.calcParams.downpayment > 1)
                  return Math.round(this.calcParams.downpayment);
               return Math.round(this.calcParams.downpayment * 100);
            },
            set(value: number) {
               if (DEBUG && componentDebug)
                  console.log('downpaymentWhole', value);
               this.calcParams.downpayment = value / 100;
               this.calcParams.loanAmount = Math.trunc(this.calcParams.purchaseprice * (1 - this.calcParams.downpayment));
            }
         },
         loanAmount: {
            get(): string {
               if (this.isExistingInvestment && this.purchaseFacts.loan?.amount)
                  return formatters.number(this.purchaseFacts.loan.amount);
               if (this.calcParams.loanAmount)
                  return formatters.number(this.calcParams.loanAmount);
               return '';
            },
            set(value) {
               if (this.isExistingInvestment) return;
               if (value === '')
                  this.calcParams.loanAmount = 0;
               this.calcParams.loanAmount = Math.trunc(formatters.parse(value));
               this.calcParams.downpayment = ((this.calcParams.purchaseprice - this.calcParams.loanAmount) / this.calcParams.purchaseprice)
            }
         },
         interestRate: {
            get(): string {
               if (this.isExistingInvestment)
                  return formatters.number((this.purchaseFacts.loan?.interestRate ?? 0) * 100, 2);
               return formatters.number(this.calcParams.interestrate, 2);
            },
            set(value) {
               if (this.isExistingInvestment) return;
               this.calcParams.interestrate = formatters.parse(value);
            }
         },
         interestRateWhole: {
            get(): string {
               if (this.isExistingInvestment)
                  return formatters.number((this.purchaseFacts.loan?.interestRate ?? 0) * 100, 2);
               return formatters.number(this.calcParams.interestrate * 100, 2);
            },
            set(value) {
               if (this.isExistingInvestment) return;
               this.calcParams.interestrate = formatters.parse(value) / 100;
            }
         },
         loanTerm: {
            get(): string {
               if (this.isExistingInvestment)
                  return formatters.number(this.purchaseFacts.loan?.term ?? 0);
               return formatters.number(this.calcParams.loanterm);
            },
            set(value) {
               if (this.isExistingInvestment) return;
               if (typeof value == 'number')
                  this.calcParams.loanterm = value;
               else
                  this.calcParams.loanterm = formatters.parse(value);
            }
         },
         loanFees: {
            get(): string {
               return formatters.number(this.calcParams.loanfees, 1);
            },
            set(value) {
               this.calcParams.loanfees = formatters.parse(value);
            }
         },
         loanFeesWhole: {
            get(): string {
               return formatters.number(this.calcParams.loanfees * 100, 1);
            },
            set(value) {
               this.calcParams.loanfees = formatters.parse(value) / 100;
            }
         },
         titleCharges: {
            get(): string {
               return formatters.number(this.calcParams.titlecharges);
            },
            set(value) {
               this.calcParams.titlecharges = formatters.parse(value);
            }
         },
         strRentalRate: {
            get(): string {
               return formatters.number(this.calcParams.strRentalRate);
            },
            set(value) {
               this.calcParams.strRentalRate = formatters.parse(value);
            }
         },
         strRehabAmount: {
            get(): string {
               return formatters.number(this.calcParams.strRehabAmount);
            },
            set(value) {
               this.calcParams.strRehabAmount = formatters.parse(value);
            }
         },
         strWorkingCapitalReserve: {
            get(): string {
               return formatters.number(this.calcParams.strWorkingCapitalReserve);
            },
            set(value) {
               this.calcParams.strWorkingCapitalReserve = formatters.parse(value);
            }
         },
         strRehabDays: {
            get(): string {
               return formatters.number(this.calcParams.strRehabDays);
            },
            set(value) {
               this.calcParams.strRehabDays = formatters.parse(value);
            }
         },
         strArv: {
            get(): string {
               return formatters.number(this.calcParams.strArv);
            },
            set(value) {
               this.calcParams.strArv = formatters.parse(value);
            }
         },
         strMaintenance: {
            get(): string {
               return formatters.number(this.calcParams.strMaintenance * 100, 1);
            },
            set(value) {
               this.calcParams.strMaintenance = formatters.parse(value) / 100;
            }
         },
         strFurnishingExpenses: {
            get(): string {
               return formatters.number(this.calcParams.strFurnishingExpenses);
            },
            set(value) {
               this.calcParams.strFurnishingExpenses = formatters.parse(value);
            }
         },
         strPropertyInsurance: {
            get(): string {
               if (this.calcParams.strPropertyInsurance != undefined)
                  return formatters.number(this.calcParams.strPropertyInsurance);
               return formatters.number(this.calculatedResults?.shortTermSummary?.propertyInsurance);
            },
            set(value) {
               if (value == '')
                  this.calcParams.strPropertyInsurance = undefined;
               else
                  this.calcParams.strPropertyInsurance = formatters.parse(value);
            }
         },
         strConsumableExpenses: {
            get(): string {
               return formatters.number(this.calcParams.strConsumableExpenses);
            },
            set(value) {
               this.calcParams.strConsumableExpenses = formatters.parse(value);
            }
         },
         strUtilityExpenses: {
            get(): string {
               return formatters.number(this.calcParams.strUtilityExpenses);
            },
            set(value) {
               this.calcParams.strUtilityExpenses = formatters.parse(value);
            }
         },
         strOtherExpense: {
            get(): string {
               return formatters.number(this.calcParams.strOtherExpense);
            },
            set(value) {
               this.calcParams.strOtherExpense = formatters.parse(value);
            }
         },
         strCleaningExpenses: {
            get(): string {
               return formatters.number(this.calcParams.strCleaningExpenses);
            },
            set(value) {
               this.calcParams.strCleaningExpenses = formatters.parse(value);
            }
         },
         strPlatformServiceFees: {
            get(): string {
               return formatters.number(this.calcParams.strPlatformServiceFees * 100, 1);
            },
            set(value) {
               this.calcParams.strPlatformServiceFees = formatters.parse(value) / 100;
            }
         },
         strManagementFee: {
            get(): string {
               return formatters.number(this.calcParams.strManagementFee * 100, 1);
            },
            set(value) {
               this.calcParams.strManagementFee = formatters.parse(value) / 100;
            }
         },
         strAnnualRentalGrowth: {
            get(): string {
               return formatters.number(this.calcParams.strAnnualRentalGrowth * 100, 1);
            },
            set(value) {
               this.calcParams.strAnnualRentalGrowth = formatters.parse(value) / 100;
            }
         },
         strAnnualExpenseGrowth: {
            get(): string {
               return formatters.number(this.calcParams.strAnnualExpenseGrowth * 100, 1);
            },
            set(value) {
               this.calcParams.strAnnualExpenseGrowth = formatters.parse(value) / 100;
            }
         },
         strPropertyAppreciation: {
            get(): string {
               return formatters.number(this.calcParams.strPropertyAppreciation * 100, 1);
            },
            set(value) {
               this.calcParams.strPropertyAppreciation = formatters.parse(value) / 100;
            }
         },
         utilityExpenses: {
            get(): string {
               return formatters.number(this.calcParams.utilityExpenses);
            },
            set(value) {
               this.calcParams.utilityExpenses = formatters.parse(value);
            }
         },
         otherExpense: {
            get(): string {
               return formatters.number(this.calcParams.otherExpense);
            },
            set(value) {
               this.calcParams.otherExpense = formatters.parse(value);
            }
         },
         capitalExpenditures: {
            get(): string {
               if (!this.calcParams.capitalExpenditures)
                  return "0";
               return formatters.number(this.calcParams.capitalExpenditures, 1);
            },
            set(value) {
               this.calcParams.capitalExpenditures = formatters.parse(value);
            }
         },
         capitalExpendituresWhole: {
            get(): string {
               if (!this.calcParams.capitalExpenditures)
                  return "0";
               return formatters.number(this.calcParams.capitalExpenditures * 100, 1);
            },
            set(value) {
               this.calcParams.capitalExpenditures = formatters.parse(value) / 100;
            }
         },
         maintenance: {
            get(): string {
               return formatters.number(this.calcParams.maintenance, 1);
            },
            set(value) {
               this.calcParams.maintenance = formatters.parse(value);
            }
         },
         maintenanceWhole: {
            get(): string {
               return formatters.number(this.calcParams.maintenance * 100, 1);
            },
            set(value) {
               this.calcParams.maintenance = formatters.parse(value) / 100;
            }
         },
         propertyTax: {
            get(): string {
               return formatters.number(this.calcParams.propertytax);
            },
            set(value) {
               this.calcParams.propertytax = formatters.parse(value);
            }
         },
         propertyInsurance: {
            get(): string {
               return formatters.number(this.calcParams.propertyinsurance);
            },
            set(value) {
               this.calcParams.propertyinsurance = formatters.parse(value);
            }
         },
         associationFee: {
            get(): string {
               return formatters.number(this.calcParams.associationfee);
            },
            set(value) {
               this.calcParams.associationfee = formatters.parse(value);
            }
         },
         leaseCommissionWhole: {
            get(): string {
               if (this.isLeasingCommissionsPercent)
                  return formatters.number(this.calcParams.leasecommission * 100, 1);
               return formatters.number(this.calcParams.leasecommission);
            },
            set(value) {
               if (this.isLeaseRenewalFeePercent)
                  this.calcParams.leasecommission = formatters.parse(value) / 100;
               else
                  this.calcParams.leasecommission = formatters.parse(value);
            }
         },
         leaseCommission: {
            get(): string {
               return formatters.number(this.calcParams.leasecommission, 1);
            },
            set(value) {
               this.calcParams.leasecommission = formatters.parse(value);
            }
         },
         leaseRenewalFee: {
            get(): string {
               return formatters.number(this.calcParams.leaserenewalfee, 1);
            },
            set(value) {
               this.calcParams.leaserenewalfee = formatters.parse(value);
            }
         },
         leaseRenewalFeeWhole: {
            get(): string {
               if (this.isLeaseRenewalFeePercent)
                  return formatters.number(this.calcParams.leaserenewalfee * 100, 1);
               return formatters.number(this.calcParams.leaserenewalfee, 1);
            },
            set(value) {
               if (this.isLeaseRenewalFeePercent)
                  this.calcParams.leaserenewalfee = formatters.parse(value) / 100;
               else
                  this.calcParams.leaserenewalfee = formatters.parse(value);
            }
         },
         managementFee: {
            get(): string {
               if (this.isManagementFeePercent) {
                  if ((this.calcParams.managementfee * 100) % 1 != 0)
                     return formatters.number(this.calcParams.managementfee, 3);
                  else
                     return formatters.number(this.calcParams.managementfee);
               }
               return formatters.number(this.calcParams.managementfee, 1);
            },
            set(value) {
               if (this.isManagementFeePercent)
                  this.calcParams.managementfee = formatters.parse(value);
               else
                  this.calcParams.managementfee = formatters.parse(value);
            }
         },
         managementFeeWhole: {
            get(): string {
               if (this.isManagementFeePercent) {
                  if ((this.calcParams.managementfee * 100) % 1 != 0)
                     return formatters.number(this.calcParams.managementfee * 100, 1);
                  else
                     return formatters.number(this.calcParams.managementfee * 100);
               }
               return formatters.number(this.calcParams.managementfee, 1);
            },
            set(value) {
               if (this.isManagementFeePercent)
                  this.calcParams.managementfee = formatters.parse(value) / 100;
               else
                  this.calcParams.managementfee = formatters.parse(value);
            }
         },
         costOfSale: {
            get(): string {
               return formatters.number(this.calcParams.costofsale, 2);
            },
            set(value) {
               this.calcParams.costofsale = formatters.parse(value);
            }
         },
         costOfSaleWhole: {
            get(): string {
               return formatters.number(this.calcParams.costofsale * 100, 2);
            },
            set(value) {
               this.calcParams.costofsale = formatters.parse(value) / 100;
            }
         },
         annualRentalGrowth: {
            get(): string {
               return formatters.number(this.calcParams.annualrentalgrowth, 1);
            },
            set(value) {
               this.calcParams.annualrentalgrowth = formatters.parse(value);
            }
         },
         annualRentalGrowthWhole: {
            get(): string {
               return formatters.number(this.calcParams.annualrentalgrowth * 100, 1);
            },
            set(value) {
               this.calcParams.annualrentalgrowth = formatters.parse(value) / 100;
            }
         },
         annualExpenseGrowth: {
            get(): string {
               return formatters.number(this.calcParams.annualexpensegrowth, 3);
            },
            set(value: string) {
               this.calcParams.annualexpensegrowth = formatters.parse(value);
            }
         },
         annualExpenseGrowthWhole: {
            get(): string {
               return formatters.number(this.calcParams.annualexpensegrowth * 100, 1);
            },
            set(value: string) {
               this.calcParams.annualexpensegrowth = formatters.parse(value) / 100;
            }
         },
         propertyAppreciation: {
            get(): string {
               return formatters.number(this.calcParams.propertyappreciation, 3);
            },
            set(value) {
               this.calcParams.propertyappreciation = formatters.parse(value);
            }
         },
         propertyAppreciationWhole: {
            get(): string {
               return formatters.number(this.calcParams.propertyappreciation * 100, 1);
            },
            set(value) {
               this.calcParams.propertyappreciation = formatters.parse(value) / 100;
            }
         },
         //#endregion
         isRecordsLoading(): boolean {
            // TODO: Have components tell parent if they are loading or not.
            return false;
         },
         calculatingClasses(): object {
            return {
               'active': this.calculating
            };
         },
         showStatus(): boolean {
            /* Will have to add soldDate to PropertyInfo
            if (this.property.mlsSource?.isOffmarket && this.property.mlsSource?.status && this.property.mlsSource?.status.match(/sold/i)) {
                if (this.property.mlsSource?.soldDate) {
                    const diff = (new Date()).getTime() - this.property.mlsSource?.soldDate.getTime();
                    var days = diff / (1000 * 3600 * 24);
                    //console.log('showStatus', diff, days);
                    return days <= 90;
                }
            }
            */
            return this.propertyStore?.property?.mlsSource?.isOffmarket != true;
         },
         canSaveChanges(): boolean {
            return this.access
               && this.access.isAuthenticated == true
               && window.isIdx != true
         },
         showShare(): boolean {
            return !this.isShare && !this.isAnalysisRequest && !this.onBehalfOf && this.access?.isAnonymous != true;
         },
         showFavorite(): boolean {
            return !this.isShare && !this.isAnalysisRequest;
         },
         canResetParameters(): boolean {
            return !this.isShare && !this.isAnalysisRequest;
         },
         existingInvestmentButtonStyle(): object {
            return {
               'exists': this.hasExistingInvestment == true,
               'issue': this.calculatedResults.existingInvestmentNeedsAttention == true,
            };
         },
         existingInvestmentIconStyle(): object {
            return {
               'fa-light': this.hasExistingInvestment == false,
               'fa-solid': this.hasExistingInvestment == true
            };
         },
         showAddMarketplace(): boolean {
            return !this.isShare && !this.isAnalysisRequest && !this.onBehalfOf && this.access?.isClientAccount != true;
         },
         firstProformaYearRaw(): YearRawResult | undefined {
            return this.calculatedResults?.raw?.find((a) => a.year == this.calculatedResults?.firstProformaYear);
         },
         firstProformaYearNormalized(): YearNormalizedResult | undefined {
            return this.calculatedResults?.normalized?.find((a) => a.year == this.calculatedResults?.firstProformaYear);
         },
         firstProformaYearShortTerm(): YearNormalizedPreciseResult | undefined {
            return this.calculatedResults?.shortTerm?.find((a) => a.year == this.calculatedResults?.firstProformaYear);
         },
         netEquity(): number | undefined {
            if (this.isExistingInvestment == true && this.calculatedResults?.existingInvestmentSummary)
               return this.calculatedResults.existingInvestmentSummary.currentYear.netEquity;
            return this.firstProformaYearRaw.netequity;
         },
         canReport(): boolean {
            return this.reportingIssueType != undefined && this.reporting == false;
         },
         haveAccess(): boolean {
            return this.access?.isAnonymous != true || this.property?.mlsSource?.isActiveMarketplaceListing == true;
         },
         haveAccessKpi(): boolean {
            return this.isMarketplace || this.access?.properties?.financialAnalysis == true;
         },
         hasCustomEdits(): boolean {
            return this.isCustomized == true
               && this.analysis == undefined
               && window.isIdx != true;
         },
         validVirtualTourUrl() {
            return this.hasVirtualTourUrl &&
               (this.property?.mlsSource?.virtualTourUrl.trim().startsWith('http') ||
                  this.property?.mlsSource?.virtualTourUrl.trim().startsWith('//'));
         },
         hasVirtualTourUrl(): boolean {
            return this.property?.mlsSource?.virtualTourUrl != undefined &&
               (this.property?.mlsSource?.virtualTourUrl.trim() != '' &&
                  this.property?.mlsSource?.virtualTourUrl.indexOf('<') == -1 &&
                  this.property?.mlsSource?.virtualTourUrl.indexOf(':\\') == -1);
         },
         virtualTourUrl(): string | undefined {
            if (this.validVirtualTourUrl)
               return this.property?.mlsSource?.virtualTourUrl;
            else if (this.hasVirtualTourUrl)
               return 'http://' + this.property?.mlsSource?.virtualTourUrl;
            return undefined;
         },
         calculateUsingHistoricalFacts: {
            get(): boolean {
               return this.isExistingInvestment == true;
            },
            set(value: boolean) {
               this.calcParams.investmentStrategy == (value ? InvestmentStrategy.ExistingInvestment : InvestmentStrategy.NewPurchase);
            }
         },
         unitsBadgeStyle(): object {
            return {
               'text-bg-light': !this.hasUnitErrors,
               'text-bg-issue': this.hasUnitErrors
            };
         },
         statusStyle(): object {
            return {
               'badge-outline-active': /active/i.test(this.propertyStore?.property?.mlsSource?.status) == true,
               'badge-outline-pending': this.propertyStore?.property?.mlsSource?.status == 'pending',
               'badge-outline-sold': this.propertyStore?.property?.mlsSource?.status == 'sold',
               'badge-outline-new': this.propertyStore?.property?.mlsSource?.status == 'new',
               'badge-outline-closed': this.propertyStore?.property?.mlsSource?.status == 'closed',
               'badge-outline-off-market': this.propertyStore?.property?.mlsSource?.status == 'off market',
            };
         },
         allowEmptyParams(): boolean {
            return this.isExistingInvestment == true &&
               this.purchaseFacts?.years?.length > 0;
         },
         isManagementFeePercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.PropertyManagement) == StandardIncomeExpenseAccounts.PropertyManagement;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.PropertyManagement;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.PropertyManagement;
            }
         },
         isLeasingCommissionsPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.LeasingCommissions) == StandardIncomeExpenseAccounts.LeasingCommissions;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.LeasingCommissions;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.LeasingCommissions;
            }
         },
         isLeaseRenewalFeePercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.LeaseRenewalFee) == StandardIncomeExpenseAccounts.LeaseRenewalFee;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.LeaseRenewalFee;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.LeaseRenewalFee;
            }
         },
         isPlatformServiceFeesPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.PlatformServiceFees) == StandardIncomeExpenseAccounts.PlatformServiceFees;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.PlatformServiceFees;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.PlatformServiceFees;
            }
         },
         isMaintenancePercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.PropertyMaintenance) == StandardIncomeExpenseAccounts.PropertyMaintenance;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.PropertyMaintenance;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.PropertyMaintenance;
            }
         },
         isUtilityExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.UtilityExpenses) == StandardIncomeExpenseAccounts.UtilityExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.UtilityExpenses;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.UtilityExpenses;
            }
         },
         isFurnishingExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.FurnishingExpenses) == StandardIncomeExpenseAccounts.FurnishingExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.FurnishingExpenses;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.FurnishingExpenses;
            }
         },
         isConsumableExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.ConsumableExpenses) == StandardIncomeExpenseAccounts.ConsumableExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.ConsumableExpenses;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.ConsumableExpenses;
            }
         },
         isCleaningExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.CleaningExpenses) == StandardIncomeExpenseAccounts.CleaningExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.CleaningExpenses;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.CleaningExpenses;
            }
         },
         isOtherExpensePercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.OtherExpense) == StandardIncomeExpenseAccounts.OtherExpense;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.OtherExpense;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.OtherExpense;
            }
         },
         isCapitalExpendituresPercent: {
            get(): boolean {
               return (this.calcParams.percentageLongTermFields & StandardIncomeExpenseAccounts.CapitalExpenditures) == StandardIncomeExpenseAccounts.CapitalExpenditures;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageLongTermFields |= StandardIncomeExpenseAccounts.CapitalExpenditures;
               else
                  this.calcParams.percentageLongTermFields &= ~StandardIncomeExpenseAccounts.CapitalExpenditures;
            }
         },
         //

         isStrManagementFeePercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.PropertyManagement) == StandardIncomeExpenseAccounts.PropertyManagement;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.PropertyManagement;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.PropertyManagement;
            }
         },
         isStrLeasingCommissionsPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.LeasingCommissions) == StandardIncomeExpenseAccounts.LeasingCommissions;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.LeasingCommissions;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.LeasingCommissions;
            }
         },
         isStrPlatformServiceFeesPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.PlatformServiceFees) == StandardIncomeExpenseAccounts.PlatformServiceFees;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.PlatformServiceFees;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.PlatformServiceFees;
            }
         },
         isStrMaintenancePercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.PropertyMaintenance) == StandardIncomeExpenseAccounts.PropertyMaintenance;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.PropertyMaintenance;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.PropertyMaintenance;
            }
         },
         isStrUtilityExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.UtilityExpenses) == StandardIncomeExpenseAccounts.UtilityExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.UtilityExpenses;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.UtilityExpenses;
            }
         },
         isStrFurnishingExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.FurnishingExpenses) == StandardIncomeExpenseAccounts.FurnishingExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.FurnishingExpenses;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.FurnishingExpenses;
            }
         },
         isStrConsumableExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.ConsumableExpenses) == StandardIncomeExpenseAccounts.ConsumableExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.ConsumableExpenses;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.ConsumableExpenses;
            }
         },
         isStrCleaningExpensesPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.CleaningExpenses) == StandardIncomeExpenseAccounts.CleaningExpenses;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.CleaningExpenses;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.CleaningExpenses;
            }
         },
         isStrOtherExpensePercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.OtherExpense) == StandardIncomeExpenseAccounts.OtherExpense;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.OtherExpense;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.OtherExpense;
            }
         },
         isStrCapitalExpendituresPercent: {
            get(): boolean {
               return (this.calcParams.percentageShortTermFields & StandardIncomeExpenseAccounts.CapitalExpenditures) == StandardIncomeExpenseAccounts.CapitalExpenditures;
            },
            set(value: boolean) {
               if (value)
                  this.calcParams.percentageShortTermFields |= StandardIncomeExpenseAccounts.CapitalExpenditures;
               else
                  this.calcParams.percentageShortTermFields &= ~StandardIncomeExpenseAccounts.CapitalExpenditures;
            }
         },
         showOnePercentRule(): boolean {
            return this.isAdvancedMode == false || (this.isExistingInvestment && this.hasInvestmentGap == true);
         },
         currentYear(): number {
            return currentYear;
         },
         issueExistingInvestment(): boolean {
            return this.calculatedResults?.existingInvestmentNeedsAttention == true || this.requireCalculateAndSave;
         },
         rentalTypeValuesYearOne(): YearNormalizedPreciseResult {
            switch (this.calcParams.rentalType) {
               case RentalType.LongTerm:
                  return this.calculatedResults?.normalized?.find(y => y.year == this.firstProformaYear);
               case RentalType.ShortTerm:
                  return this.calculatedResults?.shortTerm?.find(y => y.year == this.firstProformaYear);
               case RentalType.PadSplit:
                  return this.calculatedResults?.padSplit?.find(y => y.year == this.firstProformaYear);
            }
         },
         firstProformaYearData(): YearNormalizedResult | undefined {
            if (this.isShortTerm)
               return this.calculatedResults?.shortTerm?.find((a) => a.year == this.firstProformaYear);
            if (this.isPadSplit)
               return this.calculatedResults?.padSplit?.find((a) => a.year == this.firstProformaYear);

            return this.calculatedResults?.normalized?.find((a) => a.year == this.firstProformaYear);
         },
         //#region Tooltip Data
         irrCashflow(): Array<number> {
            if (this.isShortTerm)
               return this.calculatedResults?.shortTermSummary?.irrCashFlow?.slice(0, this.tooltipHoldPeriod + 1) ?? [];
            if (this.isPadSplit)
               return this.calculatedResults?.padSplitSummary?.irrCashFlow?.slice(0, this.tooltipHoldPeriod + 1) ?? [];

            return this.calculatedResults?.irrCashFlow?.slice(0, this.tooltipHoldPeriod + 1) ?? [];
         },
         getHoldIrr(): number {
            return this.calculatedResults?.raw?.find(q => q.fiscalYear == (currentYear + this.tooltipHoldPeriod))?.irr ?? 0;
         },
         //#endregion
         isPortfolioInsights(): boolean {
            return this.portfolioId != undefined;
         },
         guarded(): boolean {
            return (!this.isMarketplace && !this.hasMlsEntitlements)
               && !this.isAdmin
               && !this.isUser;
         },
         
         onePercentRuleStyle(): object {
            return {
               'badge': true,
               'text-bg-success': this.onePercentRule >= 0.01,
               'text-bg-warning': this.onePercentRule < 0.01,
               'rounded-pill': true,
            };
         },

         ...mapState(usePortfolioInsightsStore, ['currentLease', 'portfolioId']),
         ...mapState(useAccountStore, ['otherExpenseAccounts', 'otherNonOperatingExpenseAccounts', 'otherIncomeAccounts', 'incomeAccounts']),
         ...mapState(useAuthStore, ['access', 'aclLoaded', 'canSaveParameters', 'hasAdvanced', 'isAnonymous', 'isShare', 'isAnalysisRequest', 'isIdx', 'account', 'showAfterTax', 'isAdmin', 'isUser', 'hasMlsEntitlements']),
         ...mapState(usePropertyStore, ['calcParams', 'calcParamsOrig', 'loading', 'monthlyRentalsFirstOriginal', 'purchaseFacts', 'calculating', 'calculatedResults', 'hasExistingInvestment', 'ignoreHistoricalOperatingIncome', 'delayedInService',
            'initialInvestment', 'initialInvestmentDescribed', 'isHoldYearAuto', 'requireUnits', 'unitsTotalRentalRate', 'askIfPreviousLoan', 'cantAutoPopulate', 'isExistingInvestment', 'hasUnitErrors', 'isMarketplace', 'discarded', 'hideIrr', 'hasInvestmentGap',
            'isSyncedExpenses', 'hasChanges', 'onePercentRule', 'isNewPurchase', 'isFlip', 'isRentVsSell', 'isPerformaceReview', 'isLongTerm', 'isShortTerm', 'isPadSplit', 'isSingleFamily', 'isMultiFamily', 'isCommercial', 'hasAlternatives',
            'firstProformaYear', 'requireCalculateAndSave', 'flipTotalCashInvested', 'flipNetProfit', 'flipParams', 'flipNetInvestmentAfterRefi', 'accumulationOfWealth', 'sharingProperty', 'isAtPurchase', 'netGainOnInvestment', 'holdYear', 'firstAnalyzedYear',
            'blueChipCriteria', 'holdPeriod', 'coc', 'cocAfterTax']),
         ...mapStores(useAuthStore, usePropertyStore, useAccountStore, usePortfolioInsightsStore),
      },
      existingInvestmentInterface: {
         editInvestment: () => { },
      },
      watch: {
         'purchaseFacts.purchasePrice'(purchasePrice) {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.watch.purchaseFacts.purchasePrice`, purchasePrice, this.calcParams.purchaseprice, this.originalPurchasePrice);
            if (!purchasePrice)
               return;
            if (this.originalPurchasePrice == undefined)
               this.originalPurchasePrice = this.calcParams.purchaseprice;
            this.calcParams.purchaseprice = purchasePrice;
         },
         'purchaseFacts.calculateUsingHistoricalFacts'(calculateUsingHistoricalFacts) {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.watch.purchaseFacts.calculateUsingHistoricalFacts`, calculateUsingHistoricalFacts, this.calcParams.purchaseprice);
            if (calculateUsingHistoricalFacts == false && this.originalPurchasePrice != undefined)
               this.calcParams.purchaseprice = this.originalPurchasePrice;
         },
         'purchaseFacts.titleCharges'(titleCharges) {
            this.calcParams.titlecharges = titleCharges;
         },
         'purchaseFacts.workingCapital'(workingCapital) {
            this.calcParams.workingCapitalReserve = workingCapital;
         },
         'purchaseFacts.rehab'(rehab) {
            this.calcParams.rehabamount = rehab;
         },
         'isShortTerm'(rehab) {
            if (!this.calculating)
               this.updateCharts();
         },
         'calcParams.propertyValueEoY'(newValue: number) {
            if (newValue < this.propertyValueMin || this.propertyValueMax < newValue)
               this.hitPropertyValueMinMax(newValue);
         },
         'calcParams.hold'(newValue?: number, oldVale?: number) {
            if (DEBUG && componentDebug)
               console.log('calcParams.hold', this.loading, newValue, oldVale);
            if (this.loading == false && oldVale && newValue != oldVale)
               this.propertyStore.setHasChanges();
         },
         'calcParams.investmentStrategy'(newValue?: InvestmentStrategy, oldVale?: InvestmentStrategy) {
            if (DEBUG && componentDebug)
               console.log('calcParams.investmentStrategy', this.loading, newValue, oldVale);
            if (this.loading == false && oldVale && newValue != oldVale) {
               this.propertyStore.setHasChanges();
               switch (oldVale) {
                  case InvestmentStrategy.RentVsSell:
                  case InvestmentStrategy.Flip:
                     this.openTab('proforma-tab');
                     break;
               }
            }
         },
         'calcParams.rentalType'(newValue?: RentalType, oldVale?: RentalType) {
            if (DEBUG && componentDebug)
               console.log('calcParams.rentalType', this.loading, newValue, oldVale);
            if (this.loading == false && oldVale && newValue != oldVale)
               this.propertyStore.setHasChanges();
            this.updateCharts();
         },
         'calcParams.rentalTypeComparisons'(newValue?: RentalTypeComparisonTypes, oldVale?: RentalTypeComparisonTypes) {
            if (DEBUG && componentDebug)
               console.log('calcParams.rentalTypeComparisons', this.loading, newValue, oldVale);
            if (this.loading == false && oldVale && newValue != oldVale)
               this.propertyStore.setHasChanges();
         },
         'calcParams.purchaseprice'(newValue?: number, oldVale?: number) {
            if (this.purchaseFacts)
               this.purchaseFacts.purchasePrice = newValue;
         },
         'propertyStore.property.propertyType'(newValue?: PropertyType, oldVale?: PropertyType) {
            if (DEBUG && componentDebug)
               console.log('propertyStore.property.propertyType', this.loading, newValue, oldVale);
            if (this.loading == false && oldVale && newValue != oldVale)
               this.propertyStore.setHasChanges();
         },
         'propertyStore.property.unitCount'(newValue?: number, oldVale?: number) {
            if (DEBUG && componentDebug)
               console.log('propertyStore.property.unitCount', this.loading, newValue, oldVale);
            if (this.loading == false && oldVale && newValue != oldVale)
               this.propertyStore.setHasChanges();
         },
         'account.defaultAnalysisMode'(value: AnalysisMode) {
            this.updateCharts();
         },
         'calculatedResults.hold'() {
            this.tooltipHoldPeriod = this.calculatedResults!.hold!;
         },
         //'flipParams.afterRepairValue'(value: number) {
         //   if (!this.flipAfterRepairValueMin && value > 100) {
         //      this.hitFlipAfterRepairValueMinMax(value);
         //   }
         //},
         hasInvestmentGap() {
            this.updateCharts();
         },
         showAfterTax() {
            this.updateCharts();
         },
      },
      methods: {
         shortDate: formatters.date,
         currency: formatters.currency,
         number: formatters.number,
         percent: formatters.percent,

         longDate(date?: Date | number, nonDateValue = '') {
            return formatters.date(date, true, nonDateValue);
         },
         hitFlipAfterRepairValueMinMaxInit(value: number) {
            if (!this.flipInit) {
               if (DEBUG && componentDebug)
                  console.log('hitFlipAfterRepairValueMinMaxInit', value);
               this.hitFlipAfterRepairValueMinMax(value);
               this.purchasePriceMin = this.flipAfterRepairValueMin;
               this.purchasePriceMax = this.flipAfterRepairValueMax;
               this.flipInit = true;
            }
         },
         hitFlipAfterRepairValueMinMax(value: number) {
            if (DEBUG && componentDebug)
               console.log('hitFlipAfterRepairValueMinMax', value);
            //if (this.calcParams.investmentStrategy == InvestmentStrategy.Flip)
            this.flipAfterRepairValueMin = Math.trunc(Math.trunc(Math.trunc(value * 0.70) / 1000.0) * 1000);
            this.flipAfterRepairValueMax = Math.trunc(Math.trunc(Math.trunc(value * 1.10) / 1000.0) * 1000);
         },
         hitPurchasePriceMinMax(value: number) {
            if (DEBUG && componentDebug)
               console.log('hitPurchasePriceMinMax', value);
            this.purchasePriceMin = Math.trunc(Math.trunc(Math.trunc(value * 0.70) / 1000.0) * 1000);
            this.purchasePriceMax = Math.trunc(Math.trunc(Math.trunc(value * 1.10) / 1000.0) * 1000);
         },
         hitPropertyValueMinMax(value: number) {
            if (DEBUG && componentDebug)
               console.log('hitPropertyValueMinMax', value);
            this.propertyValueMin = Math.trunc(value * 0.70);
            this.propertyValueMax = Math.trunc(value * 1.10);
         },
         hitMonthlyRentalMinMax(value: number) {
            if (DEBUG && componentDebug)
               console.log('hitMonthlyRentalMinMax', value);
            this.monthlyRentalMin = Math.trunc(Math.trunc(Math.trunc(value * 0.70) / 25.0) * 25);
            this.monthlyRentalMax = Math.trunc(Math.trunc(Math.trunc(value * 1.10) / 25.0) * 25);
         },
         hitStrRentalMinMax(value: number) {
            if (DEBUG && componentDebug)
               console.log('hitStrRentalMinMax', value);
            this.strRentalMin = Math.trunc(value * 0.70);
            this.strRentalMax = Math.trunc(value * 1.10);
         },
         handleScroll(event) {
            // Any code to be executed when the window is scrolled
            if (window.scrollY > 90 && this.$refs.miniHeader) {
               (this.$refs.miniHeader as HTMLDivElement).classList.add('sticky');
            }
            else if (this.$refs.miniHeader) {
               (this.$refs.miniHeader as HTMLDivElement).classList.remove("sticky");
            }
         },
         refreshListing() {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.refreshListing`, this.calcParams.purchaseprice, this.calculatedResults.purchasePrice);
            try {
               document.head.children.namedItem('description').setAttribute('content', `${this.percent(this.calculatedResults.topIRR, 1)} Return; ${this.initialInvestment} Investment - Check it out on Rescover.com`)
               document.querySelector('meta[property="og:description"]').setAttribute('content', `${this.percent(this.calculatedResults.topIRR, 1)} Return; ${this.initialInvestment} Investment - Check it out on Rescover.com`)
            } catch { }
            // Update the sliders
            this.calcParams.purchaseprice = this.calculatedResults.purchasePrice;
            //this.downpayment = (r.downpayment * 100).toString();

            this.calcParams.monthlyRentalYear1 = this.calculatedResults.monthlyRentalYear1;
            if (!this.monthlyRentalMax || !this.monthlyRentalMin)
               this.hitMonthlyRentalMinMax(this.calcParams.monthlyRentalYear1);
            this.updateCharts();
            this.forceRerender();
         },
         updateCharts() {
            if (DEBUG && componentDebug)
               console.log('updateCharts', this.isShortTerm, this.loading, this.calculating, !this.calculatedResults.yr1);
            if (!this.calculatedResults.yr1)
               return;
            //#region IRR Donut
            if (this.showOnePercentRule == false) {
               if (this.hideIrr == false) {
                  this.updateGauge(this.charts.irrDonutChart, this.showAfterTax ? this.holdPeriod?.irrAfterTax : this.holdPeriod?.irr, this.propertyStore?.settings?.constraints?.minIRR);
               }
               else {
                  const config = gaugeOptions();
                  this.charts.irrDonutChart.data = config.data;
               }
            } else {
               this.updateGauge(this.charts.irrDonutChart, this.onePercentRule, 0.01, 0.001, 2);
            }
            //#endregion
            // CoC Donut
            this.updateGauge(this.charts.cocDonutChart, this.showAfterTax ? this.cocAfterTax : this.coc, this.propertyStore?.settings?.constraints?.minCoC);
            // Cap Rate Donut
            this.updateGauge(this.charts.capRateDonutChart, this.capRate, this.propertyStore?.settings?.constraints?.minCapRate);
         },
         updateGauge(chart: Chart<'doughnut', number[], string>, value?: number, goal?: number, step = 0.01, labelDecimals: number = 1) {
            let multiplier = 1;
            let labelDec = 0;
            switch (step) {
               case 0.01:
                  multiplier = 100;
                  labelDec = 0;
                  break;
               case 0.001:
                  multiplier = 1000;
                  labelDec = 1
                  break;
               default:
                  throw new Error("Step must be 0.01, 0.001");
            }

            const rules = [
               {
                  color: red,
                  startTick: -5,
                  endTick: -3,
               },
               {
                  color: yellow,
                  startTick: -3,
                  endTick: 0,
               },
               {
                  color: green,
                  startTick: 0,
                  endTick: 5,
               }
            ];
            const holeValue = Math.trunc((value ?? 0) * multiplier);
            const holeGoal = Math.trunc((goal ?? 0) * multiplier);
            const holeStep = Math.trunc(step * multiplier);

            const newRules = rules.map(r => {
               return {
                  color: r.color,
                  start: r.startTick == -5 ? undefined : holeGoal + ((r.startTick) * holeStep),
                  end: r.endTick == 5 ? undefined : holeGoal + ((r.endTick) * holeStep)
               };
            });
            const activeRule = newRules.find(q => (q.start == undefined || holeValue >= q.start) && (q.end == undefined || holeValue < q.end));
            //console.log('updateGauge', holeValue, holeGoal, holeStep, newRules, activeRule);

            let idx = 0;
            const start = holeGoal - (5 * holeStep);
            const end = holeGoal + (5 * holeStep);
            chart.data.datasets[0].label = formatters.percent(value, labelDecimals, 'N/A');

            for (let curr = start; curr <= end; curr += holeStep) {
               chart.data.labels[idx] = formatters.percent(curr / multiplier, labelDec);
               chart.data.datasets[0].backgroundColor[idx] = activeRule?.color ?? grey1;

               if (curr > holeValue)
                  chart.data.datasets[0].backgroundColor[idx] = grey1

               idx++;
            }
            chart.data.datasets[0].backgroundColor[0] = activeRule?.color ?? grey1;
            chart.update();
         },
         async saveFields() {
            if (this.canSaveParameters) {
               this.calcParamsOrig = Object.assign({}, this.calcParams);
               this.isCustomized = true;
               this.propertyStore.forceUpdateValues();
               await this.calculate(true);
            }
         },
         async saveShareListing(toSelf: boolean = false) {
            await this.saveFields();
            await this.shareListing(toSelf);
            bootstrap.Modal.getOrCreateInstance(this.$refs.modalShareChange as HTMLDivElement)?.hide();
         },
         async shareListing(toSelf: boolean = false) {
            // TODO: Show busy indicator over modal
            try {
               await this.propertyStore.shareListing(toSelf, this.email.address);

               showSuccess('Property share has been sent!');

            } catch (error) {
               showError(error as Error);
            }
            bootstrap.Modal.getOrCreateInstance(this.$refs.modalShare as HTMLDivElement)?.hide();
         },
         async saveViewPDF(event: Event) {
            await this.saveFields();
            this.buildPDF(event);
            bootstrap.Modal.getOrCreateInstance(this.$refs.modalPDF as HTMLDivElement)?.hide();
         },
         async buildPDF(event: Event, force = false) {
            try {
               if (force === false && this.hasChanges) {
                  bootstrap.Modal.getOrCreateInstance(this.$refs.modalPDF as HTMLDivElement)?.show();
                  return;
               }
               let urlPdf: string | undefined;
               if (this.onBehalfOf)
                  urlPdf = `/portfolio-insights/${window.client}/client/${this.property.propId}/pdf`;
               else {
                  let shortTerm: boolean | undefined;
                  if (this.calcParams.shortTerm)
                     shortTerm = true;
                  let resp: AxiosResponse<FileDownloadResponse, any>;
                  if (this.property.propId)
                     resp = await rescoverApi.api.pdf.property(this.property.propId, shortTerm);
                  else
                     resp = await rescoverApi.api.pdf.propertyMls(this.property.mlsSource?.shortCode, this.property?.mlsSource?.mlsNumber, shortTerm);
                  if (resp.data.status != RequestStatus.Success)
                     throw new Error(resp.data.message);
                  urlPdf = resp.data.location;
               }

               window.open(urlPdf, '_blank');
            } catch (err) {
               showError((err as Error).message);
            }
         },
         async discardFields() {
            this.discard();
            await this.calculate(false);
         },
         async discard() {
            this.discarded = true;

            await this.propertyStore.resetListing();
            if (this.calcParams?.monthlyRentalYear1 > 1)
               this.calcParams.monthlyRentalYear1 = this.monthlyRentalsFirstOriginal;
         },
         async autoCalc() {
            if (DEBUG && componentDebug)
               console.error('autoCalc');
            clearTimeout(this.typingTimer);
            await this.calculate(false);
            return false;
         },
         //#region School/Map tab
         showSchoolTab() {
            if (this.$refs.schoolsTable) {
               if ((this.$refs.schoolsTable as HTMLElement).clientHeight > 223) {
                  this.schoolMapHeight = (this.$refs.schoolsTable as HTMLElement).clientHeight;
               }
            }
         },
         //#endregion
         percentManagementFeeChanged() {
            if (this.isManagementFeePercent)
               this.calcParams.managementfee = (this.calcParams.managementfee / this.calcParams.monthlyRentalYear1);
            else
               this.calcParams.managementfee = (this.calcParams.managementfee * this.calcParams.monthlyRentalYear1);
            this.detectChange();
         },
         async detectChange() {
            if (DEBUG && componentDebug)
               console.log('detectChange', this.downpayment, this.calcParams.downpayment);
            // Force loan amount to be updated
            this.downpayment = (this.calcParams.downpayment * 100).toString();
            this.propertyStore.setHasChanges();
            await this.calculate(false);
         },
         async purchasePriceChanged() {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.purchasePriceChanged`);
            this.originalPurchasePrice = undefined;
            await this.rangesFinished();
         },
         async rangesFinished() {
            if (DEBUG && componentDebug)
               console.log('rangesFinished', this.downpayment, this.calcParams.downpayment);
            this.propertyStore.forceUpdateValues();
            this.propertyStore.setHasChanges();
            await this.calculate(false);
         },
         infoPopover(event: MouseEvent) {

            if (!this.offCanvasEl) {
               this.offCanvasEl = document.querySelector('#offcanvasScrolling');
               this.offCanvasEl?.addEventListener('hidden.bs.offcanvas', this.resetInfoPopover);
            }
            if (this.offCanvasEl) {
               this.tooltipEl = event.target as HTMLElement;
               const infoPopTitle = this.tooltipEl.dataset.title;
               const infoPopTarget = this.tooltipEl.dataset.pop;
               this.infoPopContent = this.tooltipEl.querySelector(`.info-pop`);
               // Backward compatible when .info-pop is not a child of the tooltip
               this.infoPopContent ??= this.tooltipEl.parentElement.querySelector(`.info-pop-${infoPopTarget}`);
               const offCanvasContentEl = this.offCanvasEl.querySelector('.offcanvas-info');

               if (this.infoPopContent) {
                  offCanvasContentEl.appendChild(this.infoPopContent);
                  this.infoPopContent.classList.remove('d-none');
               } else {
                  offCanvasContentEl.innerHTML = this.tooltipEl.dataset.content;
               }

               this.offCanvasEl.querySelector('.offcanvas-title').innerHTML = infoPopTitle;

               bootstrap.Offcanvas.getOrCreateInstance(this.offCanvasEl)?.show();
            }
         },
         resetInfoPopover() {
            if (this.offCanvasEl && this.infoPopContent && this.tooltipEl) {
               this.infoPopContent.classList.add('d-none');
               this.tooltipEl.appendChild(this.infoPopContent);
            } else {
               console.error('One of the tooltip elements is not set!');
            }
         },
         async resetListing(removeExistingInvestmentData: boolean = false) {
            try {
               if (this.canSaveParameters) {
                  await this.propertyStore.resetListing(removeExistingInvestmentData);

                  this.hitStrRentalMinMax(this.calcParams.strRentalRate);
                  this.isCustomized = false;
                  showSuccess('Property model values reset to original property values.');
               } else {
                  await this.propertyStore.resetListing(removeExistingInvestmentData);
                  showSuccess('Property model values reset to original shared values.');
               }
            } catch (err) {
               showError(err as Error);
            }
         },
         openRecordsTab() {
            this.openTab('records-tab');
         },
         closeInfoPop() {
            document.body.classList.remove('modal-infopop');
         },
         async updateMissingValue() {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.updateMissingValue`, this.missingValueModalType);
            switch (this.missingValueModalType) {
               case MissingValueType.RentalRate:
                  this.calcParams.monthlyRentalYear1 = this.missingValueModalValue;
                  this.hitMonthlyRentalMinMax(this.calcParams.monthlyRentalYear1);
                  await this.calculate(false);
                  break;
               case MissingValueType.SqFt:
                  this.property.facts.squareFootage = this.missingValueModalValue;
                  window.listingValues.SqFt = this.missingValueModalValue;
                  await this.calculate(false);
                  break;
               case MissingValueType.PurchasePrice:
                  this.calcParams.purchaseprice = this.missingValueModalValue;
                  this.downpayment = this.downpayment; // Force calc loan amount
                  await this.calculate(false);
                  break;
            }
         },
         editPropDetails() {
            this.$nextTick(() => {
               /* @ts-ignore */
               this.$refs.propDetails.showDialog();
            });
         },
         async updatePropDetails(details: PropertyDetailsModel) {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.updatePropDetails`, details);
            const needsCalculate = window.listingValues.SqFt != details.sqFt;
            this.property.facts.squareFootage = details.sqFt;
            window.listingValues.SqFt = details.sqFt;
            if (needsCalculate)
               await this.calculate(false);
         },
         async calculate(save: boolean) {
            if (DEBUG && componentDebug)
               console.log(`${this.$options.name}.calculate`, save);
            if (save == true && this.canSaveParameters == false) {
               let message = "Your not allowed to save shared listings, please view the property while using your own account.";
               if (this.analysis != undefined) {
                  message = "Your not allowed to save request analysis listings, please contact the agent who you requested the analysis from to make edits.";
               }
               showError(message);
            }

            if (window.listingValues?.SqFt == 0) {
               console.warn('RSC:SqFt is 0', window.listingValues?.SqFt, this.access.isAnonymous);
               if (this.access.isAnonymous) {
                  this.missingValueModalValue = undefined;
                  this.missingValueModalType = MissingValueType.SqFt;
                  this.missingValueModalTitle = 'Missing Square Footage';
                  this.missingValueModalDesc = 'Dwelling\'s estimated Square Footage:<br /> (Create an account to save this update permanently.)';
                  bootstrap.Modal.getOrCreateInstance(this.$refs.modalMissing as HTMLDivElement).show();
               }
               else {
                  this.editPropDetails();
               }
               return;
            }

            if (this.calcParams.monthlyRentalYear1 == undefined || this.calcParams.monthlyRentalYear1 == 0) {
               console.warn('RSC:Rental Rate is 0', this.calcParams.monthlyRentalYear1);
               if (this.property?.facts?.squareFootage && this.property?.facts?.squareFootage > 0) {
                  this.calcParams.monthlyRentalYear1 = Math.trunc(this.property.facts.squareFootage * 0.90);
                  this.calcParamsOrig.monthlyRentalYear1 = this.calcParams.monthlyRentalYear1;
                  this.hitMonthlyRentalMinMax(this.calcParams.monthlyRentalYear1);
               }
               else {
                  this.missingValueModalValue = undefined;
                  this.missingValueModalType = MissingValueType.RentalRate;
                  this.missingValueModalTitle = 'Unknwon rental rate';
                  this.missingValueModalDesc = 'Dwelling\'s estimated rental rate:';
                  bootstrap.Modal.getOrCreateInstance(this.$refs.modalMissing as HTMLDivElement).show();
                  return;
               }
            }

            if ((!this.property?.financial?.propertyValue || this.property?.financial?.propertyValue == 0) &&
               (!this.calcParams.purchaseprice || this.calcParams.purchaseprice == 0)) {
               console.warn('RSC:Purchase Price is 0', this.property?.financial?.propertyValue, this.calcParams.purchaseprice);
               this.missingValueModalValue = undefined;
               this.missingValueModalType = MissingValueType.PurchasePrice;
               this.missingValueModalTitle = 'Missing Purchase Price';
               this.missingValueModalDesc = 'Dwelling\'s estimated purchase price:';
               bootstrap.Modal.getOrCreateInstance(this.$refs.modalMissing as HTMLDivElement).show();
               return;
            }

            bootstrap.Modal.getOrCreateInstance(this.$refs.modalMissing as HTMLDivElement).hide();

            try {
               const saved = await this.propertyStore.calculate(save);


               if (save && saved == false) {
                  showError("Failed to save parameters.");
               }
               if (saved) {
                  this.isCustomized = true;
                  showSuccess("Your settings were saved");
               }

               this.discarded = false;
            } catch (err) {
               if (Exception.hasRescoverError(err) && err.errorId == ErrorIds.ExpenseAccountError)
                  this.openTab('parameters-tab')
               else if (!Exception.isRescoverError(err) || !err.handled)
                  showError(err as Error);
            }
         },
         async favoriteListing() {
            try {
               const message = await this.propertyStore.favoriteListing();
               showSuccess(message);
            }
            catch (error) {
               showError(error as Error);
            }
         },
         async ignoreListing() {
            try {
               const message = await this.propertyStore.ignoreListing();
               showSuccess(message);
            }
            catch (error) {
               showError(error as Error);
            }
         },
         manageMarketplace() {
            if (window.myMarketplace)
               window.location.href = `/marketplace/${this.property.propId}/edit`;
            else
               $('#modalAddMarketplace').modal('show');
         },
         async editInvestment() {
            try {
               await this.propertyStore.initPurchaseFacts();
               bootstrap.Modal.getOrCreateInstance('#modalExistingInvestment')?.show();
            } catch (error) {
               showError(error as Error);
            }
         },
         async runCalculator(save: boolean = false) {
            this.propertyStore.forceUpdateValues();
            await this.calculate(save === true);
         },
         forceRerender() {
            this.renderKey += 1;
         },
         showReportDialog() {
            if (!this.modalReport && this.$refs.modalReport) {
               this.modalReport = new bootstrap.Modal(this.$refs.modalReport as HTMLDivElement);
            }
            if (this.modalReport)
               this.modalReport.show();
         },
         async reportProperty() {
            try {
               this.reporting = true;
               const model: ReportPropertyModel = {
                  type: this.reportingIssueType
               };
               const resp = await rescoverApi.api.property.report(window.listingValues.PropId, model);
               if (resp.data.status != RequestStatus.Success)
                  throw new Error(resp.data.message);
               this.reportingIssueType = undefined;
            }
            catch (error) {
               showError("Failed reporting " + (error as Error).message);
            }
            this.reporting = false;;
            this.modalReport.hide();
         },
         getMonthlyRental(value) {
            this.calcParams.monthlyRentalYear1 = formatters.parse(value);
         },
         async getBranding() {
            try {
               if (this.haveAccess) {

                  let resp: AxiosResponse<BrandingResponse, any>;
                  if (window.listingValues?.MlsSource?.toLowerCase() == 'rescover')
                     resp = await rescoverApi.api.accountBranding.getBrandingMls(window.listingValues.MlsSource, window.listingValues.MlsNumber);
                  else
                     resp = await rescoverApi.api.accountBranding.getBranding();

                  if (resp.data.status != RequestStatus.Success)
                     throw new Error(resp.data.message);
                  this.branding = resp.data.branding;
               }
            }
            catch (error) {
               console.error(`Get Branding`, error);
            }
         },
         openTab(tabId: string) {
            showTab(tabId);
         },
         hasLongTerm(accountId: number): boolean {
            return this.calcParams?.otherExpenses?.longTerm?.some(q => q.id == accountId) == true;
         },
         checkQueryString() {
            const urlParams = new URLSearchParams(window.location.search);
            this.isUsingCustomFinancialParams = urlParams.has('search') && urlParams.get('search') === '1';
         },
         //#region Tooltip Data
         getHoldNetEquity(idxYear: number) {
            if (idxYear == this.tooltipHoldPeriod) {
               if (this.isShortTerm)
                  return this.calculatedResults?.shortTerm?.find(q => q.fiscalYear == this.holdYear)?.netEquity;
               if (this.isPadSplit)
                  return this.calculatedResults?.padSplit?.find(q => q.fiscalYear == this.holdYear)?.netEquity;

               return this.calculatedResults?.raw?.find(q => q.fiscalYear == this.holdYear)?.netEquity;
            }
            return undefined;
         },
         getIrrYear(idxYear: number): number {
            if (idxYear == 0)
               return 0;
            return ((this.firstAnalyzedYear > 0 ? this.firstAnalyzedYear : now.getFullYear()) + (idxYear - 1));
         },
         //#endregion
      },
      created() {
         if (DEBUG && componentDebug)
            console.log(`${this.$options.name}.created`, this.analysis, this.share);

         if (DEBUG && componentDebug)
            console.log('keys', this.apiKey, this.analysis, this.share);

         this.$gmapApiPromiseLazy();

         this.$gmapApiPromiseLazy();

         if (window.client) {
            rescoverApi.api.clientId = window.client;
            this.clientId = window.client;
            this.onBehalfOf = true;
         }
         if (window.portfolioId) {
            // TODO: Handle failure
            this.portfolioInsightsStore.loadPortfolio(window.portfolioId, true)
               .then(() => {
                  this.portfolioId = window.portfolioId;
                  this.portfolioName = this.portfolioInsightsStore.portfolioName;
               });
         }

         this.propertyStore.addCalculatorEventListener(this.refreshListing);
         this.accountStore.initializeAccount(false, window.mls ?? window.listingValues.MlsSource);

         // Load things here that do not need to wait for DoM to be loaded.
         this.handleDebouncedScroll = _.debounce(this.handleScroll, 10);
         window.addEventListener('scroll', this.handleDebouncedScroll);

         this.checkQueryString();
      },
      beforeUnmount() {
         window.removeEventListener('scroll', this.handleDebouncedScroll);
      },
      mounted() {
         if (this.isShare)
            rescoverApi.api.homes.trackShare(this.property.propId);

         if (this.$refs.listingMapTab)
            (this.$refs.listingMapTab as HTMLElement).addEventListener('shown.bs.tab', this.showSchoolTab);

         document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((e) => new bootstrap.Tooltip(e));

         if (window.query_changes)
            this.propertyStore.setHasChanges();

         //#region Donut Charts
         // Build cap rate, cash on cash and irr charts
         this.charts = {
            capRateDonutChart: new Chart((this.$refs.capScaleChart as HTMLCanvasElement), gaugeOptions()),
            cocDonutChart: new Chart((this.$refs.cocScaleChart as HTMLCanvasElement), gaugeOptions()),
            irrDonutChart: new Chart((this.$refs.irrScaleChart as HTMLCanvasElement), gaugeOptions()),
         };
         //#endregion

         // Gallery options
         Fancybox.bind('[data-fancybox="gallery"]', {});

         if (DEBUG && componentDebug)
            console.log(`${this.$options.name}.mounted`, this.analysisRequestId);

         if (window.isIdx)
            this.propertyStore.setIdx();

         let useLedger = window.portfolioId ? true : undefined;

         this.aclLoaded
            .then(() => {
               this.propertyStore.initialize(window.listingValues.PropId, window.listingValues.MlsSource, window.listingValues.MlsNumber, useLedger)
                  .then(() => {
                     this.property.isCustom = this.propertyStore.property.isCustom;
                     // Force range slider's min/max values to recalculate
                     this.hitMonthlyRentalMinMax(this.calcParams.monthlyRentalYear1);
                     this.hitStrRentalMinMax(this.calcParams.strRentalRate);
                     if (this.flipParams?.afterRepairValue)
                        this.hitFlipAfterRepairValueMinMaxInit(this.flipParams.afterRepairValue);
                     else
                        this.hitPurchasePriceMinMax(this.calcParams.purchaseprice);
                  })
                  .then(async () => {
                     await this.calculate(false);
                  })
                  .then(() => {
                     if (window.location.hash == '#ConfirmInvestmentDetails')
                        this.showInvestmentDetails = true;
                  })
                  .catch((err) => {
                     showError(err as Error);
                  });
            })
            .then(async () => await this.accountStore.fetchUserSettings())
            .then(async () => await this.accountStore.fetchIncomeExpenseAccounts())
            .then(async () => await this.getBranding());

         //#region Charts for Market area
         if ($('#market').length) {

            //sales market chart
            let eSalesMarketChart = document.getElementById('salesMarketChart');
            if (eSalesMarketChart) {
               let ctx = (eSalesMarketChart as HTMLCanvasElement).getContext('2d');
               let salesMarketChart = new Chart(ctx, {
                  type: 'bar',
                  data: {
                     labels: ['12/30/2019 - 1/5/2020',
                        '1/6/2020 - 1/12/2020',
                        '1/13/2020 - 1/19/2020',
                        '1/20/2020 - 1/26/2020',
                        '1/27/2020 - 2/2/2020',
                        '2/3/2020 - 2/9/2020',
                        '2/10/2020 - 2/16/2020',
                        '2/17/2020 - 2/23/2020',
                        '2/24/2020 - 3/1/2020',
                        '3/2/2020 - 3/8/2020',
                        '3/9/2020 - 3/15/2020',
                        '3/16/2020 - 3/22/2020',
                        '3/23/2020 - 3/29/2020',
                        '3/30/2020 - 4/5/2020',
                        '4/6/2020 - 4/12/2020'],
                     datasets: [{
                        label: 'Median Sales Price',
                        type: 'line',
                        fill: false,
                        borderColor: 'rgb(54,110,190)',
                        borderWidth: 1,
                        yAxisID: 'y',
                        data: [279957,
                           293120,
                           299000,
                           274500,
                           282318,
                           289900,
                           275000,
                           295420,
                           312500,
                           324750,
                           305000,
                           299476,
                           284900,
                           282250,
                           289450]
                     },
                     {
                        label: 'Median Sales $/SF',
                        type: 'line',
                        fill: false,
                        borderColor: 'rgb(248,115,18)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                        data: [112.51,
                           117.24,
                           118.71,
                           119.45,
                           117.79,
                           115.17,
                           116.65,
                           116.86,
                           118.68,
                           119.86,
                           118.40,
                           123.40,
                           117.07,
                           117.68,
                           122.63]
                     },
                     {
                        label: 'Median Sold Price ',
                        type: 'line',
                        fill: false,
                        borderColor: 'rgb(248,198,0)',
                        borderWidth: 1,
                        yAxisID: 'y',
                        data: [258185,
                           260000,
                           263750,
                           262500,
                           267750,
                           264500,
                           239500,
                           246850,
                           264500,
                           255000,
                           250000,
                           268000,
                           251490,
                           266816,
                           250000,]
                     },
                     //{
                     //    label: '5 Week MA',
                     //    type: 'line',
                     //    backgroundColor: 'rgb(107,191,72)',
                     //    data: [,
                     //        ,
                     //        ,
                     //        ,
                     //        262437,
                     //        263700,
                     //        259600,
                     //        256220,
                     //        256620,
                     //        254070,
                     //        251170,
                     //        256870,
                     //        257798,
                     //        258261,
                     //        257261]
                     //},
                     {
                        label: 'Median $/SF Sold',
                        type: 'line',
                        fill: false,
                        borderColor: 'rgb(38,154,228)',
                        borderWidth: 1,
                        yAxisID: 'y',
                        data: [113.26,
                           112.24,
                           108.48,
                           110.88,
                           109.78,
                           105.53,
                           103.23,
                           105.84,
                           112.64,
                           110.17,
                           110.00,
                           111.30,
                           108.93,
                           111.66,
                           108.60]
                     },
                     {
                        label: '# of New Listings',
                        type: 'bar',
                        backgroundColor: 'rgb(166,170,172)',
                        yAxisID: 'y1',
                        data: [138,
                           260,
                           229,
                           219,
                           275,
                           299,
                           286,
                           284,
                           316,
                           362,
                           327,
                           342,
                           301,
                           242,
                           201]
                     },
                     {
                        label: '# of Sold Listings',
                        type: 'bar',
                        backgroundColor: 'rgb(107,191,72)',
                        yAxisID: 'y1',
                        data: [209,
                           99,
                           114,
                           92,
                           174,
                           129,
                           156,
                           166,
                           252,
                           188,
                           195,
                           193,
                           183,
                           217,
                           114]
                     },
                     {
                        label: 'Inventory Accumulation',
                        type: 'bar',
                        backgroundColor: 'rgb(22,66,127)',
                        yAxisID: 'y1',
                        data: [-71,
                           90,
                           205,
                           332,
                           433,
                           603,
                           733,
                           851,
                           915,
                           1089,
                           1221,
                           1370,
                           1488,
                           1513,
                           1600]
                     }]
                  },
                  options: {
                     maintainAspectRatio: false,
                     plugins: {
                        legend: {
                           display: true,
                           position: 'bottom',
                           //    maxSize: {
                           //        height: 100
                           //    }
                        } as LegendOptions<'bar'>
                     },
                     scales: {
                        y: {
                           //id: 'y',
                           type: 'linear',
                           display: true,
                           //beginAtZero: true
                        },
                        y1: {
                           //id: 'y1',
                           type: 'linear',
                           display: true,
                           position: 'right'
                        }
                     },
                  }
               });
            }
            //rental market chart
            let eRentalMarketChart = document.getElementById('rentalMarketChart');
            if (eRentalMarketChart) {
               let ctx = (eRentalMarketChart as HTMLCanvasElement).getContext('2d');
               let rentalMarketChart = new Chart(ctx, {
                  type: 'bar',
                  data: {
                     labels: ['12/30/2019 - 1/5/2020',
                        '1/6/2020 - 1/12/2020',
                        '1/13/2020 - 1/19/2020',
                        '1/20/2020 - 1/26/2020',
                        '1/27/2020 - 2/2/2020',
                        '2/3/2020 - 2/9/2020',
                        '2/10/2020 - 2/16/2020',
                        '2/17/2020 - 2/23/2020',
                        '2/24/2020 - 3/1/2020',
                        '3/2/2020 - 3/8/2020',
                        '3/9/2020 - 3/15/2020',
                        '3/16/2020 - 3/22/2020',
                        '3/23/2020 - 3/29/2020',
                        '3/30/2020 - 4/5/2020',
                        '4/6/2020 - 4/12/2020'],
                     datasets: [
                        {
                           label: 'Median Rental Rate',
                           type: 'line',
                           fill: false,
                           borderColor: 'rgb(54,110,190)',
                           borderWidth: 1,
                           yAxisID: 'y',
                           data: [1695,
                              1775,
                              1675,
                              1750,
                              1650,
                              1650,
                              1650,
                              1700,
                              1800,
                              1700,
                              1799,
                              1785,
                              1675,
                              1700,
                              1690]
                        },
                        {
                           label: 'Median Leased Rate',
                           type: 'line',
                           fill: false,
                           borderColor: 'rgb(248,198,0)',
                           borderWidth: 1,
                           yAxisID: 'y',
                           data: [1600,
                              1663,
                              1650,
                              1650,
                              1650,
                              1600,
                              1725,
                              1573,
                              1650,
                              1600,
                              1700,
                              1645,
                              1650,
                              1650,
                              1650]
                        },
                        {
                           label: '# of New Rentals',
                           type: 'bar',
                           backgroundColor: 'rgb(166,170,172)',
                           yAxisID: 'y1',
                           data: [126,
                              105,
                              123,
                              89,
                              114,
                              116,
                              96,
                              101,
                              91,
                              124,
                              119,
                              100,
                              105,
                              118,
                              151]
                        },
                        {
                           label: '# of Leased Rentals',
                           type: 'bar',
                           backgroundColor: 'rgb(107,191,72)',
                           yAxisID: 'y1',
                           data: [120,
                              60,
                              85,
                              69,
                              123,
                              77,
                              84,
                              70,
                              188,
                              90,
                              102,
                              63,
                              77,
                              116,
                              72]
                        },
                        {
                           label: 'Inventory Accumulation',
                           type: 'bar',
                           backgroundColor: 'rgb(22,66,127)',
                           yAxisID: 'y1',
                           data: [6,
                              51,
                              89,
                              109,
                              100,
                              139,
                              151,
                              182,
                              85,
                              119,
                              136,
                              173,
                              201,
                              203,
                              282]
                        }]
                  },
                  options: {
                     maintainAspectRatio: false,
                     plugins: {
                        legend: {
                           display: true,
                           position: 'bottom',
                           //    maxSize: {
                           //        height: 100
                           //    }
                        } as LegendOptions<'bar'>
                     },
                     scales: {
                        y: {
                           //id: 'y',
                           type: 'linear',
                           display: true,
                           //beginAtZero: true
                        },
                        y1: {
                           //id: 'y1',
                           type: 'linear',
                           display: true,
                           position: 'right'
                        }
                     },
                  }
               });
            }
         }
         //#endregion

         trackTabs(() => {
            this.propertyStore.propertyInitialized
               .then(() => {
                  if (this.isPortfolioInsights)
                     showTab("asset-review");
                  else
                     showTab("property-information");
               });
         });
      }
   });
</script>
